import { useEffect } from 'react';
import WebApp from '@twa-dev/sdk';
import { lightTokens, darkTokens, type ThemeTokens } from '../theme/presets';

interface ThemeProviderProps {
  children: React.ReactNode;
}

// Helper function to convert camelCase to kebab-case for CSS variables
const toKebabCase = (str: string) => str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

export function ThemeProvider({ children }: ThemeProviderProps) {
  useEffect(() => {
    const applyTheme = () => {
      // It's important to check if WebApp.colorScheme is available.
      // If not, it might mean the SDK is not fully initialized or it's an environment where it won't be set.
      const currentScheme = WebApp.colorScheme || 'light'; // Default to light if undefined
      const selectedTokens: ThemeTokens = currentScheme === 'dark' ? darkTokens : lightTokens;

      Object.entries(selectedTokens).forEach(([key, value]) => {
        document.documentElement.style.setProperty(`--color-${toKebabCase(key)}`, value);
      });

      document.documentElement.setAttribute('data-theme', currentScheme);

      if (selectedTokens.bgPrimary) {
        document.body.style.backgroundColor = selectedTokens.bgPrimary;
      } else {
        console.warn('bgPrimary token not found in selected theme for body background.');
      }
    };

    // Call WebApp.ready() to signal the app is ready for interaction if it has such a function
    // and it's meant to be called here. Based on typical SDK patterns, it might be called once at app root.
    // For theme application, we ensure it's applied once and then on changes.

    // Apply the theme as soon as this component is mounted and SDK might be ready.
    // The `themeChanged` event will handle subsequent updates from Telegram.
    applyTheme(); // Initial application

    WebApp.onEvent('themeChanged', applyTheme);

    // Optional: If WebApp.ready() is a function that takes a callback for when SDK is truly ready.
    // if (typeof WebApp.ready === 'function') {
    //   WebApp.ready(() => {
    //     applyTheme(); // Apply once the SDK signals it's ready
    //     WebApp.onEvent('themeChanged', applyTheme);
    //   });
    // } else {
    //   // If WebApp.ready() is not a callback registration, apply immediately and listen.
    //   applyTheme();
    //   WebApp.onEvent('themeChanged', applyTheme);
    // }

    return () => {
      WebApp.offEvent('themeChanged', applyTheme);
    };
  }, []); // Empty dependency array: runs once on mount, cleans up on unmount.

  return <>{children}</>;
}